import type { AdminVendor, AdminVenue } from '../types/admin';
import type { Vendor, VendorCategory } from '../types/vendors';
import type { Venue } from '../types/venues';

export function transformAdminVendorToVendor(adminVendor: AdminVendor): Vendor {
  const pricing = adminVendor.pricing_structure || {};
  const primaryCategory = (adminVendor.service_categories?.[0] || 'planners') as VendorCategory;

  return {
    id: adminVendor.id,
    name: adminVendor.name,
    category: primaryCategory,
    location: adminVendor.address,
    city: extractCityFromAddress(adminVendor.address),
    priceRange: {
      min: pricing.min_price || 0,
      max: pricing.max_price || 100000,
      unit: pricing.unit || 'per event',
      currency: pricing.currency || 'LKR'
    },
    rating: adminVendor.rating || 4.5,
    reviews: adminVendor.review_count || 0,
    image: adminVendor.photos?.[0] || getDefaultVendorImage(primaryCategory),
    specialties: adminVendor.service_categories || [],
    description: `Professional ${adminVendor.service_categories?.join(', ')} services. Contact: ${adminVendor.phone}`,
    verified: !!adminVendor.business_license,
    contact: {
      phone: adminVendor.phone,
      email: adminVendor.email,
      website: adminVendor.website || undefined
    }
  };
}

export function transformAdminVenueToVenue(adminVenue: AdminVenue): Venue {
  const capacity = adminVenue.capacity_details || {};
  const pricing = adminVenue.pricing_structure || {};

  return {
    id: adminVenue.id,
    name: adminVenue.name,
    location: adminVenue.address,
    city: extractCityFromAddress(adminVenue.address),
    price: pricing.base_price || 0,
    capacity: capacity.max_capacity || 100,
    rating: 4.5,
    reviews: 0,
    image: adminVenue.photos?.[0] || getDefaultVenueImage(),
    features: [
      ...(adminVenue.amenities || []),
      ...(adminVenue.facilities || [])
    ].slice(0, 5),
    amenities: adminVenue.amenities || [],
    description: `Beautiful venue with capacity for ${capacity.max_capacity || 100} guests. ${(adminVenue.amenities || []).slice(0, 3).join(', ')}.`,
    venueType: determineVenueType(adminVenue),
    contact: {
      phone: adminVenue.contact_phone,
      email: adminVenue.contact_email
    }
  };
}

function extractCityFromAddress(address: string): string {
  const cities = ['Colombo', 'Kandy', 'Galle', 'Negombo', 'Nuwara Eliya', 'Bentota', 'Anuradhapura', 'Jaffna', 'Trincomalee'];

  for (const city of cities) {
    if (address.toLowerCase().includes(city.toLowerCase())) {
      return city;
    }
  }

  return 'Colombo';
}

function determineVenueType(venue: AdminVenue): 'indoor' | 'outdoor' | 'hybrid' {
  const amenities = (venue.amenities || []).map(a => a.toLowerCase());
  const hasOutdoor = amenities.some(a => a.includes('outdoor') || a.includes('garden') || a.includes('beach'));
  const hasIndoor = amenities.some(a => a.includes('indoor') || a.includes('hall') || a.includes('ballroom'));

  if (hasOutdoor && hasIndoor) return 'hybrid';
  if (hasOutdoor) return 'outdoor';
  return 'indoor';
}

function getDefaultVendorImage(category: VendorCategory): string {
  const images: Record<string, string> = {
    photographers: 'https://images.unsplash.com/photo-1606216794074-735e91aa2c92?w=800',
    djs: 'https://images.unsplash.com/photo-1571609704837-ef61e4754e45?w=800',
    caterers: 'https://images.unsplash.com/photo-1555244162-803834f70033?w=800',
    florists: 'https://images.unsplash.com/photo-1487070183336-b863922373d4?w=800',
    decorators: 'https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=800',
    transport: 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?w=800',
    planners: 'https://images.unsplash.com/photo-1511578314322-379afb476865?w=800',
    attire: 'https://images.unsplash.com/photo-1594035910387-fea47794261f?w=800',
    makeup: 'https://images.unsplash.com/photo-1487412947147-5cebf100ffc2?w=800',
    videographers: 'https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?w=800'
  };

  return images[category] || images.planners;
}

function getDefaultVenueImage(): string {
  return 'https://images.unsplash.com/photo-1519167758481-83f29da8c16a?w=800';
}
